// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Grid,
    Typography,
    Stack,
    useTheme,
    useMediaQuery,
    LinearProgress,
    Paper,
    Divider,
    Table as MuiTable,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    alpha,
    Autocomplete,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import {
    StarOutline,
    TrendingUp,
    EmojiEvents,
    MonetizationOn,
    PeopleAlt,
    TrendingDown,
    CreditCard,
    AccountBalanceWallet,
    Storefront,
    PersonAdd,
} from "@mui/icons-material";
import { AppCard, Loading, Button } from "../../../components";
import { useGetSalesmanSummary } from "../../../hooks";
import dayjs from "dayjs";
import { useForm, Controller } from "react-hook-form";
import { MonthPickerControl } from "../../molecules/form-fields/MonthPickerControl";
import { RegionPieChart, TerritoryBarChart } from "./components";
import { GoldIcon } from "../../../assets/icons";

const SidebarCard = ({ title, value, gradient, icon: Icon }: { title: string, value: string | number, gradient: string, icon: any }) => (
    <Paper
        elevation={0}
        sx={{
            background: gradient,
            color: 'white',
            p: 2.5,
            mb: 2,
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            position: 'relative',
            overflow: 'hidden',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'scale(1.02)' }
        }}
    >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 1.2, opacity: 0.9 }}>
                    {title}
                </Typography>
                <Icon sx={{ opacity: 0.8, fontSize: 24 }} />
            </Stack>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: -1, textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                {typeof value === 'number' ? value.toLocaleString('tr-TR') : value}
            </Typography>
        </Box>
        <Box sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            zIndex: 0
        }} />
    </Paper>
);

const SidebarTurnoverCard = ({ title, target, actual, sx = {} }: { title: string; target: number; actual: number; sx?: any }) => {

    const percentage = target ? (actual / target) * 100 : 0;
    const profit = actual - target;

    return (
        <Paper
            elevation={0}
            sx={{
                background: "linear-gradient(135deg,#1e293b,#334155)",
                color: "white",
                p: 3,
                borderRadius: 2,
                boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                ...sx
            }}
        >
            <Stack spacing={2}>

                {/* Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                        variant="caption"
                        sx={{ textTransform: "uppercase", letterSpacing: 1.2, opacity: .8 }}
                    >
                        {title}
                    </Typography>

                    <TrendingUp sx={{ opacity: .8 }} />
                </Stack>

                {/* Actual */}
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    {actual.toLocaleString("tr-TR")} ₺
                </Typography>

                {/* Target */}
                <Typography variant="body2" sx={{ opacity: .8 }}>
                    Hedef: {target.toLocaleString("tr-TR")} ₺
                </Typography>

                {/* Progress */}
                <LinearProgress
                    variant="determinate"
                    value={Math.min(percentage, 100)}
                    sx={{
                        height: 8,
                        borderRadius: 5,
                        backgroundColor: "rgba(255,255,255,0.2)",
                        "& .MuiLinearProgress-bar": {
                            backgroundColor: percentage >= 100 ? "#22c55e" : "#38bdf8"
                        }
                    }}
                />

                {/* Bottom Stats */}
                <Stack direction="row" justifyContent="space-between">

                    <Box>
                        <Typography variant="caption" sx={{ opacity: .7 }}>
                            Gerçekleşme
                        </Typography>
                        <Typography fontWeight={700}>
                            {percentage.toFixed(1)}%
                        </Typography>
                    </Box>

                    <Box textAlign="right">
                        <Typography variant="caption" sx={{ opacity: .7 }}>
                            Fark
                        </Typography>
                        <Typography fontWeight={700} color={profit >= 0 ? "#22c55e" : "#f87171"}>
                            {profit.toLocaleString("tr-TR")} ₺
                        </Typography>
                    </Box>

                </Stack>

            </Stack>
        </Paper>
    );
};

const MetricCard = ({
    title,
    value,
    target,
    percent,
    unit = "",
    color = "primary",
    icon: Icon
}: {
    title: string,
    value: number,
    target?: number,
    percent?: number,
    unit?: string,
    color?: any,
    icon?: any
}) => {

    const theme = useTheme();

    const calculatedPercent =
        percent ?? (target ? (value / target) * 100 : null);

    const diff = target !== undefined ? value - target : null;

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 3,
                height: '100%',
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[3]
                }
            }}
        >
            <Stack spacing={1.5}>

                {/* HEADER */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.7 }}>
                        {title}
                    </Typography>

                    {Icon && (
                        <Icon sx={{ fontSize: 18, opacity: 0.7 }} />
                    )}
                </Stack>

                {/* MAIN VALUE */}
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    {value.toLocaleString('tr-TR')}{unit}
                </Typography>

                {/* TARGET */}
                {target !== undefined && (
                    <Typography variant="body2" sx={{ opacity: 0.6 }}>
                        Hedef: {target.toLocaleString('tr-TR')}{unit}
                    </Typography>
                )}

                {/* BADGE ROW */}
                {(calculatedPercent !== null || diff !== null) && (
                    <Stack direction="row" spacing={1}>

                        {/* % BADGE */}
                        {calculatedPercent !== null && (
                            <Box
                                sx={{
                                    px: 1.2,
                                    py: 0.3,
                                    borderRadius: 1,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    bgcolor: calculatedPercent >= 100
                                        ? "rgba(34,197,94,0.12)"
                                        : "rgba(59,130,246,0.12)",
                                    color: calculatedPercent >= 100
                                        ? "#16a34a"
                                        : "#2563eb"
                                }}
                            >
                                %{calculatedPercent.toFixed(0)}
                            </Box>
                        )}

                        {/* DIFF BADGE */}
                        {diff !== null && (
                            <Box
                                sx={{
                                    px: 1.2,
                                    py: 0.3,
                                    borderRadius: 1,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    bgcolor: diff >= 0
                                        ? "rgba(34,197,94,0.12)"
                                        : "rgba(239,68,68,0.12)",
                                    color: diff >= 0
                                        ? "#16a34a"
                                        : "#dc2626"
                                }}
                            >
                                {diff >= 0 ? "+" : ""}
                                {diff.toLocaleString('tr-TR')}
                            </Box>
                        )}

                    </Stack>
                )}

            </Stack>
        </Paper>
    );
};

const DistributionItem = ({ title, value, color, icon: Icon }: { title: string, value: any, color: string, icon?: any }) => (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, p: 1.5, borderRadius: 3, transition: 'bgcolor 0.2s', '&:hover': { bgcolor: alpha(color, 0.05) } }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', flex: 1 }}>
            {title}
        </Typography>
        <Paper
            elevation={0}
            sx={{
                bgcolor: color,
                color: 'white',
                px: 2.5,
                py: 1,
                borderRadius: '12px',
                minWidth: 100,
                textAlign: 'center',
                fontWeight: 700,
                boxShadow: `0 4px 12px ${alpha(color, 0.3)}`
            }}
        >
            {typeof value === 'number' ? value.toLocaleString('tr-TR') : value}
        </Paper>
    </Stack>
);

const _RenderNoData = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Paper elevation={0} sx={{ p: 8, textAlign: 'center', borderRadius: 6, bgcolor: 'white', border: '1px dashed #cbd5e1', maxWidth: 500 }}>
            <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: '#f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3
            }}>
                <StarOutline sx={{ fontSize: 40, color: '#94a3b8' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#334155', mb: 1 }}>
                Veri Bulunamadı
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Seçilen tarih aralığı için henüz performans verisi oluşmamış. Lütfen farklı bir dönem seçin.
            </Typography>
        </Paper>
    </Box>
);

export const SalesmanBonusDashboard = () => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const [dashboardData, setDashboardData] = useState<any>(null);
    const [personnelList, setPersonnelList] = useState<any[]>([]);
    const [view, setView] = useState<"profit" | "turnover">("profit");
    const [monthlyView, setMonthlyView] = useState<"profit" | "turnover">("profit");

    const { control, handleSubmit, watch } = useForm({
        defaultValues: {
            startDate: dayjs().startOf('year'),
            endDate: dayjs().endOf('month'),
            sharedId: null
        }
    });

    const watchedStartDate = watch("startDate");
    const watchedEndDate = watch("endDate");
    const watchedSharedId = watch("sharedId");

    const { mutate: getSummary, isLoading } = useGetSalesmanSummary();

    const onSubmit = (data: any) => {
        getSummary({
            startDate: data.startDate?.startOf('month').format("YYYY-MM-DD"),
            endDate: data.endDate?.endOf('month').format("YYYY-MM-DD"),
            sharedId: data.sharedId?.sharedId
        }, {
            onSuccess: (response) => {
                if (response.isSuccess) {
                    setDashboardData(response.data);

                    if (response.data?.listOfData) {
                        setPersonnelList(prev => {
                            const uniqueFromResponse = response.data.listOfData.reduce((acc: any[], curr: any) => {
                                const id = curr.sharedId || curr.sharedID;
                                if (id && !acc.find(p => p.sharedId === id)) {
                                    acc.push({ personnel: curr.personnel, sharedId: id });
                                }
                                return acc;
                            }, []);

                            const combined = [...prev];
                            uniqueFromResponse.forEach((p: any) => {
                                if (!combined.find(c => c.sharedId === p.sharedId)) {
                                    combined.push(p);
                                }
                            });
                            return combined.sort((a, b) => a.personnel.localeCompare(b.personnel));
                        });
                    }
                } else {
                    setDashboardData(null);
                }
            }
        });
    };

    useEffect(() => {
        handleSubmit(onSubmit)();
    }, [watchedStartDate, watchedEndDate, watchedSharedId]);

    const monthlyBreakdown = useMemo(() => {
        if (!dashboardData?.listOfData) return [];
        const months = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
        return months.map((m, i) => {
            const monthData = dashboardData.listOfData.filter(d => d.month === i + 1);
            if (monthData.length === 0) return { month: m, target: '-', actual: '-', percent: '-' };

            const target = monthData.reduce((acc, curr) =>
                acc + (monthlyView === "turnover" ? (curr.posTurnoverTarget || 0) : (curr.profitGoal || 0)), 0);
            const actual = monthData.reduce((acc, curr) =>
                acc + (monthlyView === "turnover" ? (curr.posTurnoverActual || 0) : (curr.profit || 0)), 0);

            return {
                month: m,
                target: target > 0 ? target.toLocaleString('tr-TR') : '-',
                actual: actual > 0 ? actual.toLocaleString('tr-TR') : '-',
                percent: target > 0 ? Math.round((actual / target) * 100) : 0
            };
        });
    }, [dashboardData, monthlyView]);

    const personnelSummary = useMemo(() => {
        if (!dashboardData?.listOfData) return [];
        const map = new Map();
        dashboardData.listOfData.forEach(item => {
            const key = `${item.personnel}-${item.region}`;
            if (!map.has(key)) {
                map.set(key, {
                    name: item.personnel,
                    region: item.region,
                    points: 0,
                    totalTargetPoint: 0,
                    posTurnoverPoint: 0,
                    posTurnoverTargetPoint: 0,
                    activePosCustomerPoint: 0,
                    activePosCustomerTargetPoint: 0,
                    newPosCustomerPoint: 0,
                    newPosCustomerTargetPoint: 0,
                    newCardSalesPoint: 0,
                    newCardSalesTargetPoint: 0,
                    newWalletPoint: 0,
                    newWalletTargetPoint: 0,
                    targetPercent: 0,
                    bonus: 0,
                    count: 0
                });
            }
            const entry = map.get(key);
            entry.points += item.totalPoint || 0;
            entry.totalTargetPoint += item.totalTargetPoint || 0;
            entry.posTurnoverPoint += item.posTurnoverPoint || 0;
            entry.posTurnoverTargetPoint += item.posTurnoverTargetPoint || 0;
            entry.activePosCustomerPoint += item.activePosCustomerPoint || 0;
            entry.activePosCustomerTargetPoint += item.activePosCustomerTargetPoint || 0;
            entry.newPosCustomerPoint += item.newPosCustomerPoint || 0;
            entry.newPosCustomerTargetPoint += item.newPosCustomerTargetPoint || 0;
            entry.newCardSalesPoint += item.newCardSalesPoint || 0;
            entry.newCardSalesTargetPoint += item.newCardSalesTargetPoint || 0;
            entry.newWalletPoint += item.newWalletPoint || 0;
            entry.newWalletTargetPoint += item.newWalletTargetPoint || 0;
            entry.targetPercent += (item.posTurnoverActual / item.posTurnoverTarget) * 100 || 0;
            entry.bonus += (item.bonus || 0);
            entry.count += 1;
        });

        return Array.from(map.values()).map(v => ({
            ...v,
            points: Math.round(v.points),
            totalTargetPoint: Math.round(v.totalTargetPoint),
            posTurnoverPoint: Math.round(v.posTurnoverPoint),
            posTurnoverTargetPoint: Math.round(v.posTurnoverTargetPoint),
            activePosCustomerPoint: Math.round(v.activePosCustomerPoint),
            activePosCustomerTargetPoint: Math.round(v.activePosCustomerTargetPoint),
            newPosCustomerPoint: Math.round(v.newPosCustomerPoint),
            newPosCustomerTargetPoint: Math.round(v.newPosCustomerTargetPoint),
            newCardSalesPoint: Math.round(v.newCardSalesPoint),
            newCardSalesTargetPoint: Math.round(v.newCardSalesTargetPoint),
            newWalletPoint: Math.round(v.newWalletPoint),
            newWalletTargetPoint: Math.round(v.newWalletTargetPoint),
            targetPercent: Math.round(v.targetPercent),
            bonus: Math.round(v.bonus)
        })).sort((a, b) => b.points - a.points);
    }, [dashboardData]);

    const getProgressColor = (percent: number) => {
        if (percent >= 100) return '#10b981'; // green
        if (percent >= 80) return '#f59e0b'; // amber
        return '#ef4444'; // red
    };

    return (
        <Box sx={{ p: 2, backgroundColor: 'rgb(246 247 248 / 1) !important', minHeight: '100vh' }}>
            <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'white', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: -0.5 }}>
                    Satış Performans Paneli
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                    <MonthPickerControl control={control} id="startDate" label="Başlangıç" size="small" sx={{ width: 140 }} />
                    <MonthPickerControl control={control} id="endDate" label="Bitiş" size="small" sx={{ width: 140 }} />
                    <Controller
                        control={control}
                        name="sharedId"
                        render={({ field }) => (
                            <Autocomplete
                                {...field}
                                options={personnelList}
                                getOptionLabel={(option) => option.personnel || ""}
                                isOptionEqualToValue={(option, value) => option.sharedId === value?.sharedId}
                                onChange={(_, data) => field.onChange(data)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Personel" size="small" sx={{ width: 200 }} />
                                )}
                            />
                        )}
                    />
                    <Button text="Sorgula" variant="contained" onClick={handleSubmit(onSubmit)} sx={{ borderRadius: '12px', px: 4, textTransform: 'none', fontWeight: 700, boxShadow: '0 4px 14px rgba(0,0,0,0.1)' }} />
                </Stack>
            </Paper>

            {isLoading && <Loading />}

            {dashboardData ? (

                <Stack>
                    <Grid container spacing={2} sx={{ mb: 2 }}>

                        <Grid item xs={6} sm={2.4}>
                            <MetricCard
                                title="Yeni POS"
                                value={dashboardData.newPosCustomerActualTotal}
                                target={dashboardData.newPosCustomerTargetTotal}
                                percent={dashboardData.newPosCustomerPercent}
                                color="primary"
                                icon={Storefront}
                            />
                        </Grid>

                        <Grid item xs={6} sm={2.4}>
                            <MetricCard
                                title="Aktif POS"
                                value={dashboardData.activePosCustomerActualTotal}
                                target={dashboardData.activePosCustomerTargetTotal}
                                percent={dashboardData.activePosCustomerPercent}
                                color="info"
                                icon={TrendingUp}
                            />
                        </Grid>

                        <Grid item xs={6} sm={2.4}>
                            <MetricCard
                                title="Yeni Müşteri"
                                value={dashboardData.newPosCustomerActualTotal}
                                target={dashboardData.newPosCustomerTargetTotal}
                                percent={dashboardData.newPosCustomerPercent}
                                color="success"
                                icon={PersonAdd}
                            />
                        </Grid>

                        <Grid item xs={6} sm={2.4}>
                            <MetricCard
                                title="Yeni Kart"
                                value={dashboardData.newCardSalesActualTotal}
                                target={dashboardData.newCardSalesTargetTotal}
                                percent={dashboardData.newCardSalesPercent}
                                color="warning"
                                icon={CreditCard}
                            />
                        </Grid>

                        <Grid item xs={6} sm={2.4}>
                            <MetricCard
                                title="Kurumsal Cüzdan"
                                value={dashboardData.newWalletActualTotal}
                                target={dashboardData.newWalletTargetTotal}
                                percent={dashboardData.newWalletPercent}
                                color="secondary"
                                icon={AccountBalanceWallet}
                            />
                        </Grid>

                    </Grid>

                    <Grid container spacing={2}>
                        {/* Sidebar Section */}
                        <Grid item xs={12} md={3}>
                            <Stack spacing={2} sx={{ height: 500 }}>
                                <SidebarTurnoverCard
                                    title="POS Ciro Durumu"
                                    target={dashboardData.posTurnoverTargetTotal}
                                    actual={dashboardData.posTurnoverActualTotal}
                                    sx={{ flex: 1 }}
                                />

                                <SidebarTurnoverCard
                                    title="POS Kar Durumu"
                                    target={dashboardData.posProfitTargetTotal}
                                    actual={dashboardData.posProfitActualTotal}
                                    sx={{ flex: 1 }}
                                />
                            </Stack>

                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <AppCard sx={{ borderRadius: 5, p: 0, height: 500, display: 'flex', flexDirection: 'column' }}>
                                <TerritoryBarChart dashboardData={dashboardData} view={view} setView={setView} />
                            </AppCard>
                        </Grid>
                        <Grid item xs={12} lg={5} md={5}>
                            <AppCard sx={{ p: 0, overflow: 'hidden', borderRadius: 5, height: 500, display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Aylık Performans</Typography>
                                    <ToggleButtonGroup
                                        value={monthlyView}
                                        exclusive
                                        onChange={(_, newValue) => newValue && setMonthlyView(newValue)}
                                        size="small"
                                    >
                                        <ToggleButton value="profit">POS Karı</ToggleButton>
                                        <ToggleButton value="turnover">Ciro</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                                    <MuiTable size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', color: '#64748b' }}>AY</TableCell>
                                                <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', color: '#64748b' }}>HEDEF ({monthlyView === "turnover" ? "CİRO" : "KAR"})</TableCell>
                                                <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', color: '#64748b' }}>GERÇEKLEŞME ({monthlyView === "turnover" ? "CİRO" : "KAR"})</TableCell>
                                                <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', color: '#64748b' }}>BAŞARI %</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {monthlyBreakdown.map((row, idx) => (
                                                <TableRow key={idx} hover>
                                                    <TableCell sx={{ fontWeight: 600, color: '#334155' }}>{row.month}</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.target}</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#1e293b' }}>{row.actual}</TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" alignItems="center" spacing={1.5}>
                                                            <Box sx={{ flex: 1, minWidth: 80 }}>
                                                                <LinearProgress
                                                                    variant="determinate"
                                                                    value={typeof row.percent === 'number' ? Math.min(row.percent, 100) : 0}
                                                                    sx={{
                                                                        borderRadius: 4,
                                                                        height: 8,
                                                                        bgcolor: alpha(getProgressColor(row.percent as number), 0.2),
                                                                        '& .MuiLinearProgress-bar': {
                                                                            bgcolor: getProgressColor(row.percent as number),
                                                                            borderRadius: 4
                                                                        }
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 35, color: getProgressColor(row.percent as number) }}>
                                                                %{row.percent}
                                                            </Typography>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </MuiTable>
                                </TableContainer>
                            </AppCard>
                        </Grid>

                        {/* <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} lg={12}>
                                <AppCard sx={{ height: '100%', borderRadius: 5, display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 3 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Bölgesel Ciro Dağılımı</Typography>
                                    <RegionPieChart dashboardData={dashboardData} />
                                </AppCard>
                            </Grid>
                        </Grid> */}

                        {/* Main Content Area */}
                        <Grid item xs={12} md={12}>
                            <Stack spacing={3}>

                                {/* Middle row with performance table and metrics */}
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <AppCard sx={{ p: 0, overflow: 'hidden', borderRadius: 5 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, p: 2.5, pb: 1 }}>Personel Performans Sıralaması</Typography>
                                            <TableContainer sx={{ maxHeight: 500 }}>
                                                <MuiTable size="small" stickyHeader>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', color: '#64748b' }}>İSİM</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, bgcolor: alpha('#0891b2', 1), color: 'white' }}>BÖLGE</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, bgcolor: alpha('#4f46e5', 1), color: 'white', textAlign: 'center' }}>CİRO</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, bgcolor: alpha('#0ea5e9', 1), color: 'white', textAlign: 'center' }}>AKTİF POS</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, bgcolor: alpha('#10b981', 1), color: 'white', textAlign: 'center' }}>YENİ POS</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, bgcolor: alpha('#f59e0b', 1), color: 'white', textAlign: 'center' }}>YENİ KART</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, bgcolor: alpha('#8b5cf6', 1), color: 'white', textAlign: 'center' }}>CÜZDAN</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, bgcolor: alpha('#1e293b', 1), color: 'white', textAlign: 'center' }}>TOPLAM</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, bgcolor: alpha('#16a34a', 1), color: 'white', textAlign: 'right' }}>PRİM</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {personnelSummary.map((row, idx) => (
                                                            <TableRow key={idx} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                                <TableCell sx={{ fontWeight: 600, color: '#334155' }}>{row.name}</TableCell>
                                                                <TableCell sx={{ color: '#0891b2', fontWeight: 600, fontSize: '0.85rem', bgcolor: alpha('#0891b2', 0.02) }}>{row.region}</TableCell>
                                                                <TableCell sx={{ textAlign: 'center', bgcolor: alpha('#4f46e5', 0.02) }}>
                                                                    <Typography sx={{ fontWeight: 700, color: '#4f46e5', lineHeight: 1.2 }}>{row.posTurnoverPoint}</Typography>
                                                                    <Typography variant="caption" sx={{ opacity: 0.5, fontWeight: 500 }}>Hedef: {row.posTurnoverTargetPoint}</Typography>
                                                                </TableCell>
                                                                <TableCell sx={{ textAlign: 'center', bgcolor: alpha('#0ea5e9', 0.02) }}>
                                                                    <Typography sx={{ fontWeight: 700, color: '#0ea5e9', lineHeight: 1.2 }}>{row.activePosCustomerPoint}</Typography>
                                                                    <Typography variant="caption" sx={{ opacity: 0.5, fontWeight: 500 }}>Hedef: {row.activePosCustomerTargetPoint}</Typography>
                                                                </TableCell>
                                                                <TableCell sx={{ textAlign: 'center', bgcolor: alpha('#10b981', 0.02) }}>
                                                                    <Typography sx={{ fontWeight: 700, color: '#10b981', lineHeight: 1.2 }}>{row.newPosCustomerPoint}</Typography>
                                                                    <Typography variant="caption" sx={{ opacity: 0.5, fontWeight: 500 }}>Hedef: {row.newPosCustomerTargetPoint}</Typography>
                                                                </TableCell>
                                                                <TableCell sx={{ textAlign: 'center', bgcolor: alpha('#f59e0b', 0.02) }}>
                                                                    <Typography sx={{ fontWeight: 700, color: '#f59e0b', lineHeight: 1.2 }}>{row.newCardSalesPoint}</Typography>
                                                                    <Typography variant="caption" sx={{ opacity: 0.5, fontWeight: 500 }}>Hedef: {row.newCardSalesTargetPoint}</Typography>
                                                                </TableCell>
                                                                <TableCell sx={{ textAlign: 'center', bgcolor: alpha('#8b5cf6', 0.02) }}>
                                                                    <Typography sx={{ fontWeight: 700, color: '#8b5cf6', lineHeight: 1.2 }}>{row.newWalletPoint}</Typography>
                                                                    <Typography variant="caption" sx={{ opacity: 0.5, fontWeight: 500 }}>Hedef: {row.newWalletTargetPoint}</Typography>
                                                                </TableCell>
                                                                <TableCell sx={{ textAlign: 'center', bgcolor: alpha('#1e293b', 0.02) }}>
                                                                    <Typography sx={{ fontWeight: 900, color: '#1e293b', fontSize: '0.9rem', lineHeight: 1.2 }}>{row.points}</Typography>
                                                                    <Typography variant="caption" sx={{ opacity: 0.5, fontWeight: 500 }}>Hedef: {row.totalTargetPoint}</Typography>
                                                                </TableCell>
                                                                <TableCell sx={{ fontWeight: 800, fontFamily: 'monospace', textAlign: 'right', color: '#16a34a', bgcolor: alpha('#16a34a', 0.02) }}>
                                                                    {row.bonus.toLocaleString('tr-TR')} ₺
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </MuiTable>
                                            </TableContainer>
                                        </AppCard>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Stack spacing={3}>


                                            <AppCard sx={{ borderRadius: 5, background: 'linear-gradient(to right, #ffffff, #f8fafc)' }}>
                                                <Grid container spacing={4} alignItems="center">
                                                    <Grid item xs={12} sm={5}>
                                                        <Box sx={{ textAlign: 'center' }}>
                                                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#64748b', letterSpacing: 1 }}>TOPLAM DAĞITILAN</Typography>
                                                            <Typography variant="h3" sx={{ fontWeight: 900, color: '#10b981', mt: 1, textShadow: '0 2px 10px rgba(16, 185, 129, 0.2)' }}>
                                                                {personnelSummary.reduce((acc, c) => acc + c.bonus, 0).toLocaleString('tr-TR')}
                                                                <Typography component="span" variant="h5" sx={{ ml: 1, fontWeight: 700, color: '#10b981' }}>TL</Typography>
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} sm={7}>
                                                        <DistributionItem title="Prim Alan Satıcı Sayısı" value={personnelSummary.filter(p => p.bonus > 0).length} color="#6366f1" />
                                                        <DistributionItem title="70 Puan Altı Alan" value={personnelSummary.filter(p => p.points < 70).length} color="#ef4444" />
                                                        <DistributionItem title="Hedef Gerçekleştiren (100%+)" value={personnelSummary.filter(p => p.targetPercent >= 100).length} color="#64748b" />
                                                    </Grid>
                                                </Grid>
                                            </AppCard>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Stack>
                        </Grid>
                    </Grid>
                </Stack>

            ) : (
                _RenderNoData()
            )}
        </Box>
    );
};
